import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
} from '@mui/material';
import {
  CheckCircle,
  Close,
  Celebration,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Modal de Éxito Super Cool y Profesional
 *
 * @param {boolean} open - Estado de apertura del modal
 * @param {function} onClose - Función para cerrar el modal
 * @param {string} title - Título principal
 * @param {string} message - Mensaje descriptivo
 * @param {string} type - Tipo de acción: 'create' o 'update'
 */
const SuccessModal = ({ open, onClose, title, message, type = 'create' }) => {
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (open) {
      setShowConfetti(true);
      // Auto cerrar después de 3.5 segundos
      const timer = setTimeout(() => {
        onClose();
      }, 3500);
      return () => clearTimeout(timer);
    } else {
      setShowConfetti(false);
    }
  }, [open, onClose]);

  const isCreate = type === 'create';
  const bgGradient = isCreate
    ? 'linear-gradient(135deg, #1976d2 0%, #2196f3 100%)'
    : 'linear-gradient(135deg, #42a5f5 0%, #1976d2 100%)';

  return (
    <AnimatePresence>
      {open && (
        <Dialog
          open={open}
          onClose={onClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '24px',
              overflow: 'visible',
              background: 'transparent',
              boxShadow: 'none',
            }
          }}
        >
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            exit={{ scale: 0, rotate: 180 }}
            transition={{
              type: 'spring',
              stiffness: 260,
              damping: 20
            }}
          >
            <DialogContent
              sx={{
                p: 0,
                position: 'relative',
                overflow: 'visible',
                background: bgGradient,
                borderRadius: '24px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              }}
            >
              {/* Botón cerrar */}
              <IconButton
                onClick={onClose}
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  color: 'white',
                  bgcolor: 'rgba(255,255,255,0.2)',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.3)',
                  },
                  zIndex: 2,
                }}
                size="small"
              >
                <Close />
              </IconButton>

              {/* Confetti de fondo */}
              {showConfetti && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    overflow: 'hidden',
                    borderRadius: '24px',
                    pointerEvents: 'none',
                  }}
                >
                  {[...Array(20)].map((_, i) => (
                    <motion.div
                      key={i}
                      initial={{
                        x: Math.random() * 100 - 50,
                        y: -20,
                        rotate: 0,
                        opacity: 1
                      }}
                      animate={{
                        x: Math.random() * 400 - 200,
                        y: 600,
                        rotate: 360 * (Math.random() > 0.5 ? 1 : -1),
                        opacity: 0
                      }}
                      transition={{
                        duration: 2 + Math.random() * 2,
                        delay: Math.random() * 0.5,
                        ease: 'easeOut'
                      }}
                      style={{
                        position: 'absolute',
                        left: `${Math.random() * 100}%`,
                        top: 0,
                        width: 10,
                        height: 10,
                        background: ['#FFD700', '#FF69B4', '#00CED1', '#FF6347', '#7FFF00'][Math.floor(Math.random() * 5)],
                        borderRadius: Math.random() > 0.5 ? '50%' : '0%',
                      }}
                    />
                  ))}
                </Box>
              )}

              {/* Contenido principal */}
              <Box
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  p: 6,
                  textAlign: 'center',
                }}
              >
                {/* Icono principal con animación */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    delay: 0.2,
                    type: 'spring',
                    stiffness: 200,
                    damping: 10
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      position: 'relative',
                      mb: 3,
                    }}
                  >
                    {/* Anillo pulsante */}
                    <motion.div
                      animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.5, 0, 0.5],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut'
                      }}
                      style={{
                        position: 'absolute',
                        top: -10,
                        left: -10,
                        right: -10,
                        bottom: -10,
                        borderRadius: '50%',
                        border: '3px solid white',
                      }}
                    />

                    {/* Icono de check */}
                    <CheckCircle
                      sx={{
                        fontSize: 100,
                        color: 'white',
                        filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.3))',
                      }}
                    />

                    {/* Icono de celebración */}
                    <motion.div
                      initial={{ scale: 0, rotate: -45 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{
                        delay: 0.4,
                        type: 'spring',
                        stiffness: 200
                      }}
                      style={{
                        position: 'absolute',
                        top: -15,
                        right: -15,
                      }}
                    >
                      <Celebration
                        sx={{
                          fontSize: 40,
                          color: '#FFD700',
                          filter: 'drop-shadow(0 2px 10px rgba(0,0,0,0.3))',
                        }}
                      />
                    </motion.div>
                  </Box>
                </motion.div>

                {/* Título */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: 'white',
                      mb: 2,
                      textShadow: '0 2px 20px rgba(0,0,0,0.2)',
                      letterSpacing: '-0.5px',
                    }}
                  >
                    {title || (isCreate ? '¡Creado con Éxito!' : '¡Actualizado con Éxito!')}
                  </Typography>
                </motion.div>

                {/* Mensaje */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'rgba(255,255,255,0.95)',
                      mb: 4,
                      fontSize: '1.1rem',
                      fontWeight: 500,
                      lineHeight: 1.6,
                    }}
                  >
                    {message || 'La operación se completó exitosamente.'}
                  </Typography>
                </motion.div>

                {/* Botón de acción */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  <Button
                    variant="contained"
                    onClick={onClose}
                    sx={{
                      bgcolor: 'white',
                      color: '#1976d2',
                      px: 4,
                      py: 1.5,
                      borderRadius: '50px',
                      fontWeight: 700,
                      fontSize: '1rem',
                      textTransform: 'none',
                      boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.9)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 12px 28px rgba(0,0,0,0.25)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    ¡Genial!
                  </Button>
                </motion.div>

                {/* Barra de progreso de auto-cierre */}
                <motion.div
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 3.5, ease: 'linear' }}
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: 'rgba(255,255,255,0.3)',
                    transformOrigin: 'left',
                    borderBottomLeftRadius: '24px',
                    borderBottomRightRadius: '24px',
                  }}
                />
              </Box>
            </DialogContent>
          </motion.div>
        </Dialog>
      )}
    </AnimatePresence>
  );
};

export default SuccessModal;
